import React from 'react';
import {
	ExpandableSection,
	FormField,
	Input,
	Multiselect,
	Select,
	SpaceBetween,
	Toggle,
} from '@amzn/awsui-components-console';
import { AutoPopulationType } from '@amzn/snow-auto-population-service-typescript-client';
import MappingEditor from './MappingEditor';
import { AUTO_POPULATION_TYPES, AutoPopulationDefinition, MappingEntry } from './configureAttributeStep.types';

export interface MappingProps {
	requestItems: MappingEntry[];
	responseItems: MappingEntry[];
	onUpdateRequest: (items: MappingEntry[]) => void;
	onUpdateResponse: (items: MappingEntry[]) => void;
	requestBodyKeys: string[];
}

interface AutoPopulationPanelProps {
	editingDef: AutoPopulationDefinition;
	setEditingDef: React.Dispatch<React.SetStateAction<AutoPopulationDefinition | null>>;
	operationOptions: { label: string; value: string }[];
	onOperationChange: (operationId: string) => void;
	allAttributeIds: { label: string; value: string }[];
	mapping: MappingProps;
}

export default function AutoPopulationPanel({
	editingDef,
	setEditingDef,
	operationOptions,
	onOperationChange,
	allAttributeIds,
	mapping,
}: AutoPopulationPanelProps) {
	return (
		<SpaceBetween size="l">
			<ExpandableSection defaultExpanded variant="default" headerText="AutoPopulation">
				<SpaceBetween size="m">
					<FormField label="Auto-Population Type">
						<Select
							selectedOption={
								AUTO_POPULATION_TYPES.find((o) => o.value === editingDef.autoPopulationType) || null
							}
							options={AUTO_POPULATION_TYPES}
							onChange={({ detail }) =>
								setEditingDef({ ...editingDef, autoPopulationType: detail.selectedOption.value as AutoPopulationType })
							}
						/>
					</FormField>
					{editingDef.autoPopulationType === AutoPopulationType.EXTERNAL && (
						<FormField label="AutoPopulation Operation">
							<Select
								selectedOption={
									operationOptions.find((o) => o.value === editingDef.operationId) || null
								}
								options={operationOptions}
								placeholder="Select an operation"
								filteringType="auto"
								onChange={({ detail }) =>
									onOperationChange(detail.selectedOption.value!)
								}
							/>
						</FormField>
					)}
					{editingDef.autoPopulationType !== AutoPopulationType.NOOP && (
						<Toggle
							checked={editingDef.isOverridable ?? false}
							onChange={({ detail }) =>
								setEditingDef({ ...editingDef, isOverridable: detail.checked })
							}
						>
							Is Overridable
						</Toggle>
					)}
					<FormField label="Dependencies" description="Attributes this auto-population depends on">
						<Multiselect
							selectedOptions={(editingDef.dependencies || []).map((d) => ({ label: d, value: d }))}
							options={allAttributeIds}
							placeholder="Select dependency attributes"
							onChange={({ detail }) =>
								setEditingDef({ ...editingDef, dependencies: detail.selectedOptions.map((o) => o.value!) })
							}
						/>
					</FormField>
					<FormField label="Dependents" description="Attributes that depend on this auto-population">
						<Multiselect
							selectedOptions={(editingDef.dependents || []).map((d) => ({ label: d, value: d }))}
							options={allAttributeIds}
							placeholder="Select dependent attributes"
							onChange={({ detail }) =>
								setEditingDef({ ...editingDef, dependents: detail.selectedOptions.map((o) => o.value!) })
							}
						/>
					</FormField>
					<FormField label="Description">
						<Input
							value={editingDef.description || ''}
							placeholder="Enter description"
							onChange={({ detail }) => setEditingDef({ ...editingDef, description: detail.value })}
						/>
					</FormField>
					{editingDef.autoPopulationType === AutoPopulationType.EXTERNAL && (
						<>
							<FormField label="Request Mapping">
								<MappingEditor
									label="request mapping"
									items={mapping.requestItems}
									onUpdate={mapping.onUpdateRequest}
									info="Maps input attribute names to request parameters (e.g. EntityName → #EntityName#)"
									keyOptions={mapping.requestBodyKeys}
								/>
							</FormField>
							<FormField label="Response Mapping">
								<MappingEditor
									label="response mapping"
									items={mapping.responseItems}
									onUpdate={mapping.onUpdateResponse}
									info="Maps response fields to workflow attribute IDs (e.g. libby_id → libbyStoreId)"
									keyOptions={allAttributeIds.map((a) => a.value)}
								/>
							</FormField>
						</>
					)}
				</SpaceBetween>
			</ExpandableSection>
		</SpaceBetween>
	);
}
